import Checkbox from '@/components/Checkbox'
import IcoMoon from '@/components/IcoMoon'
import Layout, { Stack } from '@/components/Layout'
import MultiRangeSlider from '@/components/MultiRangeSlider'
import { useFeatureToggle } from '@/context/featureToggles'
import { useUI } from '@/context/ui'
import { eventsTracker } from '@/lib/eventsTracker'
import { getInitialTimeSliderHours } from '@/lib/timeSliderFilter'
import ScheduleCalendar from '@/widgets/ScheduleCalendar'
import { UIElementName } from '@getsetup-io/event-producer'
import { Tab } from '@headlessui/react'
import dayjs from 'dayjs'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import BadgeFilter from './BadgeFilter'
import * as styles from './style'

const communityFilterValue = 'isCommunityLedClass:false'
const encoreFilterValue = 'isEncore:false'

export interface Props {
  // TODO: Approach may need to change when accounting for other
  // types like Time (morning, night, etc) and Type (Single class vs series)
  // ...or maybe not? Something to keep an eye on as this evolves
  fetching: boolean
  onFilterChange?: () => void
}
interface TimeFilterState {
  sessionsIntervalStart: number
  sessionsIntervalEnd: number
}

const ScheduleFiltersMobile = ({ fetching, onFilterChange }: Props): JSX.Element => {
  const initialSessionsInterval = getInitialTimeSliderHours()
  const enableBadgeFilters = useFeatureToggle('exp_badge_filters')
  const showEncoreBadging = useFeatureToggle('showEncoreBadging')

  const { t } = useTranslation(['badge', 'common'])
  const {
    state: { schedulePage, scheduleFilters },
    dispatch,
  } = useUI()
  const [filterListKey, setFilterListKey] = useState(0)
  const [whichTab, setWhichTab] = useState<'categories' | 'guides'>('categories')
  const [timeFilter, setTimeFilter] = useState<TimeFilterState>({
    sessionsIntervalStart: schedulePage.sessionsIntervalStart,
    sessionsIntervalEnd: schedulePage.sessionsIntervalEnd,
  })
  const [selectedDate, setSelectedDate] = useState<Date>(dayjs(schedulePage.day).toDate())
  const [selectedFilters, setSelectedFilters] = useState<Array<string>>(schedulePage.facets)

  const hasActiveFilters = useMemo(() => {
    return (
      dayjs().isSame(selectedDate, 'day') === false ||
      selectedFilters.length > 0 ||
      timeFilter.sessionsIntervalStart !== initialSessionsInterval.start ||
      timeFilter.sessionsIntervalEnd !== initialSessionsInterval.end
    )
  }, [selectedDate, selectedFilters.length, timeFilter.sessionsIntervalStart, timeFilter.sessionsIntervalEnd])

  // Reset all date and facet filters
  function handleResetFilters(): void {
    setSelectedDate(dayjs().toDate())
    setSelectedFilters([])
    setTimeFilter({
      sessionsIntervalStart: initialSessionsInterval.start,
      sessionsIntervalEnd: initialSessionsInterval.end,
    })
  }

  const handleApplyFilters = useCallback(() => {
    dispatch({
      type: 'APPLY_SELECTED_SCHEDULE_FILTERS',
      payload: {
        ...schedulePage,
        day: selectedDate.toISOString(),
        dayString: dayjs(selectedDate).format(),
        index: 0,
        reset: true,
        facets: selectedFilters,
        sessionsIntervalStart: timeFilter.sessionsIntervalStart,
        sessionsIntervalEnd: timeFilter.sessionsIntervalEnd,
      },
    })
    if (onFilterChange) onFilterChange()
  }, [selectedDate, selectedFilters, timeFilter.sessionsIntervalStart, timeFilter.sessionsIntervalEnd])

  /* Dates Selector */
  const handleOnDateChange = useCallback((date: Date) => {
    if (!date) return
    setSelectedDate(date)
  }, [])

  useEffect(() => {
    dispatch({
      type: 'SET_CALENDAR_DATE',
      payload: dayjs(selectedDate).toDate(),
    })
  }, [selectedDate])

  const dateSelector = useMemo(() => {
    return (
      <ScheduleCalendar
        disabled={fetching}
        maxDate={dayjs().startOf('day').add(30, 'day').toDate()}
        minDate={dayjs().startOf('day').toDate()}
        onChange={() => {}}
        onSelect={handleOnDateChange}
        peekNextMonth={false}
        selected={selectedDate}
        startDate={dayjs().toDate()}
        value={dayjs(dayjs(selectedDate).startOf('day')).format()}
        readOnly
      />
    )
  }, [fetching, selectedDate])
  /* Dates Selector */

  /* Facet filters */
  const handleFilterChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const selected = event.target?.value
      const facets: string[] = event.target.checked
        ? [...selectedFilters, selected]
        : selectedFilters.filter((f: string) => f !== selected)
      setSelectedFilters(facets)
    },
    [selectedFilters],
  )

  // TODO: Merge with handleFilterChange
  const handleBadgeFilterChange = useCallback(
    (checked: boolean, value: string) => {
      const facets: string[] = checked
        ? [...selectedFilters, value]
        : selectedFilters.filter((f: string) => f !== value)
      setSelectedFilters(facets)
    },
    [selectedFilters],
  )

  const categoryItems = useMemo(() => {
    if (!scheduleFilters.categories || !scheduleFilters.categories.length) return null
    setFilterListKey(filterListKey + 1)
    return scheduleFilters.categories.map(({ name }) => {
      const selected = selectedFilters.includes(`categories:${name}`)
      return (
        <li key={name}>
          <Checkbox
            flush="right"
            label={name}
            name={`categories:${name}`}
            onChange={handleFilterChange}
            reverse
            value={`categories:${name}`}
            defaultChecked={selected}
            className={`${styles.checkbox} ${selected ? styles.filterActive : ''}`}
          />
        </li>
      )
    })
  }, [scheduleFilters.categories, selectedFilters, handleFilterChange])

  const guideItems = useMemo(() => {
    if (!scheduleFilters.guides || !scheduleFilters.guides.length) return null
    setFilterListKey(filterListKey + 1)
    return scheduleFilters.guides.map(({ name }) => {
      const selected = selectedFilters.includes(`guideName:${name}`)
      return (
        <li key={name}>
          <Checkbox
            flush="right"
            label={name}
            name={`guideName:${name}`}
            onChange={handleFilterChange}
            reverse
            value={`guideName:${name}`}
            defaultChecked={selected}
            className={`${styles.checkbox} ${selected ? styles.filterActive : ''}`}
          />
        </li>
      )
    })
  }, [scheduleFilters.guides, selectedFilters, handleFilterChange])
  /* Facet filters */

  /* Tabs */
  const selectedCategories = useMemo(() => {
    return selectedFilters
      .map((filter: string) => {
        if (filter.indexOf('categories') !== -1) {
          return filter.replace('categories:', '')
        }
      })
      .filter(Boolean)
  }, [selectedFilters])

  const selectedGuides = useMemo(() => {
    return selectedFilters
      .map((filter: string) => {
        if (filter.indexOf('guideName') !== -1) {
          return filter.replace('guideName:', '')
        }
      })
      .filter(Boolean)
  }, [selectedFilters])

  const handleTabChange = (): void => {
    setWhichTab((prevState) => {
      if (prevState === 'categories') {
        return 'guides'
      } else {
        return 'categories'
      }
    })
  }
  const handleTimeFiltersChange = (sessionsIntervalStart: number, sessionsIntervalEnd: number): void => {
    setTimeFilter({ sessionsIntervalStart, sessionsIntervalEnd })
    eventsTracker.handleOnClickAndTrackEvent(UIElementName.BUTTON_SCHEDULE_TIME_SLIDER)
  }

  const tabs = useMemo(() => {
    return (
      <div className={styles.tabsWrapper}>
        <Tab.Group defaultIndex={whichTab === 'categories' ? 0 : 1} onChange={handleTabChange}>
          <Tab.List data-headlessui-tab-list>
            <Tab className="relative">
              <IcoMoon className="mr-xs" icon="grid-dots" size={22} />
              {t('search.categories', { ns: 'common' })}
              {selectedCategories.length > 0 && <span className={styles.filterCount}>{selectedCategories.length}</span>}
            </Tab>
            <Tab className="relative">
              <IcoMoon className="mr-xs" icon="users" size={22} />
              {t('search.guides', { ns: 'common' })}
              {selectedGuides.length > 0 && <span className={styles.filterCount}>{selectedGuides.length}</span>}
            </Tab>
          </Tab.List>
        </Tab.Group>
      </div>
    )
  }, [whichTab, handleTabChange])
  /* Tabs */

  return (
    <Layout className={styles.root}>
      <Stack y className={styles.filterContent}>
        <div className={`${styles.filterControls}`}>
          {hasActiveFilters ? (
            <span className="fc-primary fs-xxs fw-semi-bold cursor-pt py-sm px-md" onClick={handleResetFilters}>
              {t('reset', { ns: 'common' })}
            </span>
          ) : (
            <div></div>
          )}
          <span className="fc-primary fs-xxs fw-semi-bold cursor-pt py-sm px-md" onClick={handleApplyFilters}>
            {t('done', { ns: 'common' })}
          </span>
        </div>
        <Stack x justifyContent="center" className={styles.dateSelector}>
          {dateSelector}
        </Stack>
        <Stack y className={'px-md mb-xl'}>
          <MultiRangeSlider
            onChangeTimeFilter={handleTimeFiltersChange}
            title="Time of Day"
            min={0}
            max={24}
            step={1}
            initialStart={timeFilter.sessionsIntervalStart}
            initialEnd={timeFilter.sessionsIntervalEnd}
          />
        </Stack>

        <Stack y className="px-md">
          {enableBadgeFilters && (
            <Stack y className={'gap-xxs'} justifyContent="center">
              {showEncoreBadging && (
                <BadgeFilter
                  defaultChecked={selectedFilters.indexOf(encoreFilterValue) !== -1}
                  disabled={fetching}
                  hasActiveFilters={hasActiveFilters}
                  icon="encore"
                  iconClass={styles.encoreIcon}
                  key="encore"
                  label={t('labels.encore', { ns: 'badge' })}
                  value={encoreFilterValue}
                  onChange={handleBadgeFilterChange}
                />
              )}
              <BadgeFilter
                defaultChecked={selectedFilters.indexOf(communityFilterValue) !== -1}
                disabled={fetching}
                hasActiveFilters={hasActiveFilters}
                icon="community"
                iconClass={styles.communityIcon}
                key="community"
                label={t('labels.community', { ns: 'badge' })}
                value={communityFilterValue}
                onChange={handleBadgeFilterChange}
              />
              <br />
            </Stack>
          )}
          {tabs}
          {whichTab === 'categories' ? <ul key={`categories-${filterListKey}`}>{categoryItems}</ul> : null}
          {whichTab === 'guides' ? <ul key={`guides-${filterListKey}`}>{guideItems}</ul> : null}
        </Stack>
      </Stack>
    </Layout>
  )
}

export default ScheduleFiltersMobile
